import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';

import { RoleActions } from './role.store';
import { RoleHttpClient } from '../services/role-http-client';

@Injectable({ providedIn: 'root' })
export class RoleEffects {
  private readonly roleHttpClient = inject(RoleHttpClient);
  private readonly actions$ = inject(Actions);

  public effects = createEffectFactory(RoleActions, this.actions$);

  public fetchRoles$ = this.effects(RoleActions.fetchCollection, () =>
    this.roleHttpClient.fetchRoles().pipe(map(data => ({ data }))),
  );

  public fetchRoleById$ = this.effects(RoleActions.fetchOne, ({ payload }) =>
    this.roleHttpClient.fetchRole(payload.id).pipe(map(({ role }) => ({ data: role }))),
  );

  public fetchRolesByAbility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.fetchRolesByAbility),
      switchMap(({ payload }) =>
        this.roleHttpClient.fetchRoles(payload.scope).pipe(
          map(data =>
            RoleActions.fetchRolesByAbilitySuccess({ payload: { data, scope: payload.scope } }),
          ),
          catchError(error => of(RoleActions.fetchRolesByAbilityFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public createRole$ = this.effects(RoleActions.createOnePessimistic, ({ payload }) =>
    this.roleHttpClient.createRole(payload.data).pipe(map(({ role }) => ({ data: role }))),
  );

  public updateRole$ = this.effects(RoleActions.updateOnePessimistic, ({ payload }) =>
    this.roleHttpClient.updateRole(payload.data).pipe(map(({ role }) => ({ data: role }))),
  );

  public deleteRole$ = this.effects(RoleActions.deleteRole, ({ payload }) =>
    this.roleHttpClient.deleteRole(payload).pipe(
      map(role => RoleActions.deleteRoleSuccess({ payload: { data: role } })),
      catchError(error =>
        of(RoleActions.deleteRoleFailed({ payload: { data: payload.id, error } })),
      ),
    ),
  );

  public duplicateRole$ = this.effects(RoleActions.duplicateRole, ({ payload }) =>
    this.roleHttpClient.duplicateRole(payload).pipe(
      map(role =>
        RoleActions.duplicateRoleSuccess({
          payload: { data: role, correlationId: payload.correlationId },
        }),
      ),
      catchError(error => of(RoleActions.duplicateRoleFailed({ payload: error }))),
    ),
  );

  public saveRoleAbilities$ = this.effects(RoleActions.saveRoleAbilities, ({ payload }) =>
    this.roleHttpClient.saveRoleAbilities(payload).pipe(
      map(role => RoleActions.saveRoleAbilitiesSuccess({ payload: { data: role } })),
      catchError(error => of(RoleActions.saveRoleAbilitiesFailed({ payload: error }))),
    ),
  );

  public updatePositions$ = this.effects(RoleActions.updatePositions, ({ payload }) =>
    this.roleHttpClient.updatePositions(payload).pipe(
      map(role => RoleActions.updatePositionsSuccess({ payload: role })),
      catchError(error => of(RoleActions.updatePositionsFailed({ payload: error }))),
    ),
  );
}
